// src/components/block/liveStreamManagment/DetachedWindow.tsx
// Rend une partie de la régie dans un autre onglet du navigateur (window.open + portail React).
//
// Le composant reste monté dans l'onglet de la régie : état, flux WebRTC et graphe audio
// ne sont pas dupliqués, seul le DOM est déplacé. Fermer la régie ferme l'onglet détaché.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/** Racine où les modales (portails) du contenu détaché doivent se monter. null = document principal. */
export const PortalTargetContext = createContext<HTMLElement | null>(null);

export const usePortalTarget = (): HTMLElement => useContext(PortalTargetContext) ?? document.body;

export interface DetachedWindowHandle {
  /** Conteneur du nouvel onglet, null tant qu'il n'est pas ouvert. */
  container : HTMLElement | null;
  /** À appeler depuis un clic (sinon le navigateur bloque l'ouverture). false = bloqué. */
  open      : () => boolean;
  close     : (sync?: boolean) => void;
  /** Ramène l'onglet détaché au premier plan. */
  focus     : () => void;
}

export function useDetachedWindow(title: string): DetachedWindowHandle {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const winRef = useRef<Window | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // `sync` : fermeture venue de l'onglet lui-même. Le rapatriement doit alors se faire TOUT DE SUITE,
  // pendant que le document de l'onglet existe encore : les <video> y portent les flux qu'il faut
  // rendre à la régie. Un setState ordinaire est traité après la destruction du document.
  const close = useCallback((sync = false) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    const w = winRef.current;
    winRef.current = null;
    if (sync) flushSync(() => setContainer(null));
    else setContainer(null);
    try { if (w && !w.closed) w.close(); } catch { /* onglet déjà parti */ }
  }, []);

  const open = useCallback((): boolean => {
    if (winRef.current && !winRef.current.closed) { winRef.current.focus(); return true; }

    // Sans 3e argument, le navigateur ouvre un onglet et non une fenêtre.
    const w = window.open('', '_blank');
    if (!w) return false;
    winRef.current = w;

    const doc = w.document;
    doc.title = title;

    // Même thème / mêmes variables CSS que la régie.
    const syncRoot = () => {
      doc.documentElement.className = document.documentElement.className;
      for (const { name, value } of Array.from(document.documentElement.attributes)) {
        if (name !== 'class') doc.documentElement.setAttribute(name, value);
      }
      doc.body.className = document.body.className;
    };
    syncRoot();
    doc.body.style.margin = '0';
    doc.body.style.height = '100vh';
    doc.body.style.background = getComputedStyle(document.body).backgroundColor || '#0b1220';

    // Feuilles de style : copie initiale, puis miroir de celles ajoutées plus tard.
    const copyStyle = (node: Element) => {
      if (node.tagName === 'LINK' && (node as HTMLLinkElement).rel === 'stylesheet') {
        const l = doc.createElement('link');
        l.rel = 'stylesheet'; l.href = (node as HTMLLinkElement).href;
        doc.head.appendChild(l);
      } else if (node.tagName === 'STYLE') {
        const s = doc.createElement('style');
        s.textContent = node.textContent;
        doc.head.appendChild(s);
      }
    };
    Array.from(document.head.children).forEach(copyStyle);
    const mo = new MutationObserver(muts => {
      muts.forEach(m => m.addedNodes.forEach(n => { if (n.nodeType === 1) copyStyle(n as Element); }));
    });
    mo.observe(document.head, { childList: true });
    const rootMo = new MutationObserver(syncRoot);
    rootMo.observe(document.documentElement, { attributes: true });

    const root = doc.createElement('div');
    root.style.cssText = 'height:100vh;width:100%;';
    doc.body.appendChild(root);

    const onGone = () => close(true);
    w.addEventListener('beforeunload', onGone);
    w.addEventListener('pagehide', onGone);
    const onMainGone = () => close();
    window.addEventListener('beforeunload', onMainGone);
    const poll = window.setInterval(() => { if (w.closed) onGone(); }, 1000);

    cleanupRef.current = () => {
      mo.disconnect(); rootMo.disconnect();
      window.clearInterval(poll);
      window.removeEventListener('beforeunload', onMainGone);
      try { w.removeEventListener('beforeunload', onGone); w.removeEventListener('pagehide', onGone); } catch { /* idem */ }
    };

    setContainer(root);
    return true;
  }, [title, close]);

  const focus = useCallback(() => { try { winRef.current?.focus(); } catch { /* idem */ } }, []);

  // Démontage de la régie : ne pas laisser un onglet orphelin.
  useEffect(() => () => close(), [close]);

  return { container, open, close, focus };
}

// Logo Kleon.tv en deux images : le symbole, puis le nom (version blanche, pour fond sombre).
const LOGO_SYMBOL = 56;          // px, symbole
const LOGO_NAME_H = 34;          // px, hauteur du nom
const LOGO_NAME_R = 960 / 143;   // rapport largeur/hauteur de logo4.png
const LOGO_GAP    = 8;
const LOGO_ROW_W  = LOGO_SYMBOL + LOGO_GAP + LOGO_NAME_H * LOGO_NAME_R;   // largeur naturelle sur une ligne
const LOGO_MIN_SCALE = 0.6;      // en dessous, on passe le nom SOUS le symbole

/**
 * `responsive` : le logo s'adapte à la largeur de son conteneur. Il rétrécit un peu, et quand il
 * faudrait trop le réduire (< 60 %), le nom passe sous le symbole, centré. Sans `responsive`, il
 * garde sa taille naturelle sur une ligne (ex. posé en surimpression).
 */
export const KleonLogo: React.FC<{ style?: React.CSSProperties; responsive?: boolean }> = ({ style, responsive = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!responsive || !el) return;
    // Observateur de la fenêtre qui porte l'élément (cf. mini-vues du multiview).
    const RO = ((el.ownerDocument.defaultView as (Window & typeof globalThis) | null) ?? window).ResizeObserver;
    const ro = new RO(entries => setAvail(Math.round(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [responsive]);

  const base = `${window.location.origin}/images`;
  let stacked = false;
  let scale   = 1;
  if (responsive && avail !== null && avail < LOGO_ROW_W) {
    scale = avail / LOGO_ROW_W;
    if (scale < LOGO_MIN_SCALE) { stacked = true; scale = 1; }
  }

  // Presque aucune place (fenêtre à peine plus large que les blocs) : rien ne doit déborder sur eux.
  if (responsive && avail !== null && avail < 24) return <div ref={ref} style={{ width: '100%', height: 0 }} />;

  if (stacked) {
    const nameW  = Math.max(24, Math.min(LOGO_NAME_H * LOGO_NAME_R, (avail ?? 0)));
    const symbol = Math.min(LOGO_SYMBOL, avail ?? LOGO_SYMBOL);
    return (
      <div ref={ref} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, pointerEvents: 'none', width: '100%', ...style }}>
        <img src={`${base}/logo.svg`}  alt="Kleon.tv" style={{ height: symbol, width: symbol }} />
        <img src={`${base}/logo4.png`} alt=""         style={{ width: nameW, height: 'auto' }} />
      </div>
    );
  }
  return (
    <div ref={ref} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: LOGO_GAP * scale, pointerEvents: 'none', ...(responsive ? { width: '100%' } : {}), ...style }}>
      <img src={`${base}/logo.svg`}  alt="Kleon.tv" style={{ height: LOGO_SYMBOL * scale, width: LOGO_SYMBOL * scale }} />
      <img src={`${base}/logo4.png`} alt=""         style={{ height: LOGO_NAME_H * scale, width: 'auto' }} />
    </div>
  );
};
